import {Component, Inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {TranslatableComponent} from "src/app/core/models/TranslatableComponent";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {UntypedFormControl, Validators, ReactiveFormsModule, FormsModule} from "@angular/forms";
import {AssignmentProjectEmployee} from "src/app/core/models/AssignmentProjectEmployee";
import {ProjectService} from "src/app/core/services/services/project.service";
import {MessageService} from "src/app/core/services/services/message.service";
import {Project, ProjectName} from "src/app/core/models/Project";
import {AlertEnum} from "src/app/core/models/Enums/AlertEnum";
import {Employee} from "src/app/core/models/Employee";
import {Router} from "@angular/router";
import {AssignmentTableComponent} from "./assignment-table/assignment-table.component";
import {ProjectTimeSlice} from "src/app/core/models/ProjectTimeSlice";
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogContent} from "@angular/material/dialog";
import moment from "moment";
import {CustomizingService} from "src/app/core/services/services/customizing.service";
import {Client} from "src/app/core/config/Client";
import {AuthenticationService} from "../../../core/services/services/authentication.service";
import {CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import {MatIcon} from "@angular/material/icon";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatFormField, MatLabel, MatSuffix, MatError} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {ProjectAutocompleteComponent} from "../../autocomplete/components/project-autocomplete.component";
import {MatTooltip} from "@angular/material/tooltip";
import {MatButtonToggleGroup, MatButtonToggle} from "@angular/material/button-toggle";
import {MatSelect, MatSelectTrigger} from "@angular/material/select";
import {MatDatepickerInput, MatDatepickerToggle, MatDatepicker} from "@angular/material/datepicker";
import {OnlyNumbersDirective} from "../../inputdirectives/onlyNumbers.directive";
import {EscapeHotkeyDirective} from "../../hotkey/directives/escape-hotkey.directive";
import {SaveHotkeyDirective} from "../../hotkey/directives/save-hotkey.directive";
import {DatePipe} from "@angular/common";

@Component({
	selector: "app-extend-project-dialog",
	templateUrl: "./extend-or-move-project-dialog.component.html",
	styleUrls: ["./extend-or-move-project-dialog.component.scss"],
	imports: [
		CdkDrag,
		CdkDragHandle,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		MatProgressBar,
		MatFormField,
		MatLabel,
		MatInput,
		MatAutocompleteTrigger,
		ReactiveFormsModule,
		FormsModule,
		ProjectAutocompleteComponent,
		MatTooltip,
		MatButtonToggleGroup,
		MatButtonToggle,
		MatSuffix,
		MatSelect,
		MatSelectTrigger,
		MatOption,
		MatDatepickerInput,
		OnlyNumbersDirective,
		MatDatepickerToggle,
		MatDatepicker,
		MatError,
		AssignmentTableComponent,
		EscapeHotkeyDirective,
		SaveHotkeyDirective,
		DatePipe,
		TranslatePipe,
	],
})
export class ExtendOrMoveProjectDialogComponent extends TranslatableComponent implements OnDestroy, OnInit {
	allProjectAssignments: AssignmentProjectEmployee[] = [];
	projectStart: string;
	oldProjectEnd: string;
	oldProjectProbability: string;
	newProjectEnd = new UntypedFormControl("", {
		validators: [
			Validators.required,
			this.validateNewProjectEnd.bind(this),
			this.validateNotWithinOtherProjectTimeSlice.bind(this),
		],
	});
	newProjectStart = new UntypedFormControl("", {
		validators: [
			Validators.required,
			this.validateNewProjectStart.bind(this),
			this.validateNotWithinOtherProjectTimeSlice.bind(this),
		],
	});
	newProjectProbability = new UntypedFormControl("", {
		validators: [Validators.required, Validators.max(100), Validators.min(1)],
	});
	loading = false;
	noAssignments = false;
	project: Project;
	projectTimeSlices: ProjectTimeSlice[];
	selectedProjectTimeSlice: ProjectTimeSlice;
	moveProject = false;
	currentClient: Client;
	@ViewChild(AssignmentTableComponent)
	set newAssignmentTable(assignmentTable: AssignmentTableComponent) {
		const isNewTable = assignmentTable !== this._newAssignmentTable;
		this._newAssignmentTable = assignmentTable;
		// Der Setter wird bei jedem Query-Refresh (z.B. Tab-Wechsel) erneut aufgerufen und läuft während der
		// Change Detection. Daher nur bei einer neuen Tabelle befüllen und verzögert, damit sie neu gerendert wird.
		if (assignmentTable && isNewTable) {
			setTimeout(() => this.updateAssignmentTableRows());
		}
	}
	get newAssignmentTable(): AssignmentTableComponent {
		return this._newAssignmentTable;
	}
	private _newAssignmentTable: AssignmentTableComponent;
	private dialogDestroyed = false;
	private readonly dateFormat = "YYYY-MM-DD";
	gotUser: boolean;

	constructor(
		public dialogRef: MatDialogRef<ExtendOrMoveProjectDialogComponent>,
		protected customizingService: CustomizingService,
		protected translateService: TranslateService,
		protected _projectService: ProjectService,
		protected _messageService: MessageService,
		protected authenticationService: AuthenticationService,
		private _router: Router,
		@Inject(MAT_DIALOG_DATA) public data: Project,
	) {
		super(translateService, "ExtendOrMoveProjectDialogComponent");
		dialogRef.addPanelClass("mep-dialog");
	}

	async ngOnInit() {
		let user = await this.authenticationService.loadPrincipal();
		this.gotUser = !(user === null || user === undefined);
		if (this.gotUser) {
			if (this.data) {
				this.onProjectSelectionChanged(this.data);
			}
			this.currentClient = await this.customizingService.getCurrentClient();
		}
	}

	ngOnDestroy() {
		this.dialogDestroyed = true;
	}

	closeDialog(result?) {
		this.dialogDestroyed = true;
		this.dialogRef.close(result);
	}

	saveButtonDisable(): boolean {
		let disabled =
			this.newProjectEnd.invalid || this.loading || !this.newAssignmentTable?.selectedEmployeeAssignmentsAreValid();
		return this.moveProject ? disabled || this.newProjectStart.invalid : disabled;
	}

	saveChanges() {
		this.loading = true;

		if (this.moveProject) {
			this._projectService
				.moveProject(
					this.selectedProjectTimeSlice.id,
					this.getFormattedControlValue(this.newProjectStart),
					this.getFormattedControlValue(this.newProjectEnd),
					this.newProjectProbability.value,
					this.newAssignmentTable.getSelectedAssignmentsDto(),
				)
				.then(() => {
					this.afterExtendingOrMovingProject();
				})
				.catch(() => {
					this.loading = false;
				});
		} else {
			this._projectService
				.extendProject(
					this.project.id,
					this.newAssignmentTable.getSelectedAssignmentsDto(),
					this.getFormattedControlValue(this.newProjectEnd),
					this.newProjectProbability.value,
				)
				.then(() => {
					this.afterExtendingOrMovingProject();
				})
				.catch(() => {
					this.loading = false;
				});
		}
	}

	afterExtendingOrMovingProject() {
		this._messageService.add(this._lang("save-changes"), AlertEnum.SUCCESS);
		this.closeDialog(true);
		this._router.navigateByUrl("/project-edit/" + this.project.sharePointId);
	}

	onProjectSelectionChanged(projectName: ProjectName) {
		this.noAssignments = false;
		this.allProjectAssignments = [];
		this._projectService.getProjectData(projectName.sharePointId).subscribe((project) => {
			this.project = project;

			this.projectStart = this.project.startDate;
			this.getProjectProbabilities();
			this.getAssignmentProjectEmployees();

			this.newProjectEnd.reset();
		});
	}

	onSelectedProjectTimeSliceChanged(projectTimeSlice: ProjectTimeSlice) {
		this.selectedProjectTimeSlice = projectTimeSlice;
		this.updateSelectedProjectTimeSlice();

		this.updateAssignmentTableRows();
	}

	updateAssignmentTableRows() {
		if (this.dialogDestroyed || !this.project || !this.newAssignmentTable || this.allProjectAssignments === undefined) {
			return;
		}

		if (this.moveProject && !this.selectedProjectTimeSlice) {
			return;
		}

		if (this.moveProject) {
			this.updateAssignmentTableRowsMoveMode();
		} else {
			this.updateAssignmentTableRowsExtendMode();
		}

		this.applyProjectDatesToAssignments();
	}

	private updateSelectedProjectTimeSlice() {
		this.newProjectProbability.setValue(this.selectedProjectTimeSlice.probability);
		this.newProjectProbability.updateValueAndValidity();
	}

	onOperationChanged(moveProject: boolean) {
		this.moveProject = moveProject;
		this.newProjectStart.updateValueAndValidity();
		this.newProjectEnd.updateValueAndValidity();

		this.updateAssignmentTableRows();
	}

	updateAssignmentTableRowsExtendMode() {
		const newAssignments: any[] = [];

		const from = moment(this.oldProjectEnd);
		from.add(1, "day");

		let fromString = from.format(this.dateFormat);

		let mostRecentAssignments = {};

		this.allProjectAssignments.forEach((assignment) => {
			if (assignment.employee.id in mostRecentAssignments) {
				let previousDate = Date.parse(mostRecentAssignments[assignment.employee.id].effectiveFrom);
				let newDate = Date.parse(assignment.effectiveFrom);

				if (previousDate < newDate) {
					mostRecentAssignments[assignment.employee.id] = assignment;
				}
			} else {
				mostRecentAssignments[assignment.employee.id] = assignment;
			}
		});

		for (let key in mostRecentAssignments) {
			if (Object.prototype.hasOwnProperty.call(mostRecentAssignments, key)) {
				let assignment = mostRecentAssignments[key];

				if (!this.oldProjectEnd) {
					fromString = this.getEffectiveFrom(assignment.employee.id);
				}

				const newAssignment = {
					effectiveFrom: fromString,
					effectiveUntil: this.getFormattedControlValue(this.newProjectEnd),
					capacity: assignment.capacity,
					project: assignment.project,
					employee: assignment.employee,
					id: assignment.id,
				};

				newAssignments.push(newAssignment);
			}
		}

		this.newAssignmentTable.overwriteProjectStartForValidation(fromString);
		this.newAssignmentTable.hiddenAssignments = this.allProjectAssignments;
		this.newAssignmentTable.assignments = newAssignments;
	}

	updateAssignmentTableRowsMoveMode() {
		const selectedProjectTimeSliceFrom = Date.parse(this.selectedProjectTimeSlice.effectiveFrom);
		const selectedProjectTimeSliceUntil = this.selectedProjectTimeSlice.effectiveUntil
			? Date.parse(this.selectedProjectTimeSlice.effectiveUntil)
			: null;

		// all assignments within selected project time slice
		const assignmentsWithinSelectedTimeSlice = this.allProjectAssignments.filter(
			(assignment) =>
				Date.parse(assignment.effectiveFrom) >= selectedProjectTimeSliceFrom &&
				(!selectedProjectTimeSliceUntil || Date.parse(assignment.effectiveUntil) <= selectedProjectTimeSliceUntil),
		);
		const mostRecentAssignments = {};
		assignmentsWithinSelectedTimeSlice.forEach((assignment) => {
			const employeeId = assignment.employee.id;
			const previousAssignment = mostRecentAssignments[employeeId];

			if (!previousAssignment || Date.parse(previousAssignment.effectiveFrom) < Date.parse(assignment.effectiveFrom)) {
				mostRecentAssignments[employeeId] = assignment;
			}
		});

		// all assignments outside selected project time slice
		this.newAssignmentTable.hiddenAssignments = this.allProjectAssignments.filter(
			(assignment) => !assignmentsWithinSelectedTimeSlice.includes(assignment),
		);
		this.newAssignmentTable.assignments = Object.values(mostRecentAssignments);
	}

	/**
	 * Leere Werte werden nicht übernommen, damit die vorhandenen Werte in der Tabelle erhalten bleiben.
	 */
	updateAssignmentProjectEnd() {
		const projectEnd = this.getFormattedControlValue(this.newProjectEnd);
		if (!this.newAssignmentTable || !projectEnd) {
			return;
		}

		this.newAssignmentTable.setEffectiveUntilForAllAssignments(projectEnd);
		this.newAssignmentTable.overwriteProjectEndForValidation(projectEnd);
	}

	updateAssignmentProjectStart() {
		const projectStart = this.getFormattedControlValue(this.newProjectStart);
		if (!this.newAssignmentTable || !projectStart) {
			return;
		}

		this.newAssignmentTable.setEffectiveFromForAllAssignments(projectStart);
		this.newAssignmentTable.overwriteProjectStartForValidation(projectStart);
	}

	private applyProjectDatesToAssignments() {
		if (this.moveProject) {
			this.updateAssignmentProjectStart();
		}

		this.updateAssignmentProjectEnd();
	}

	updateValidation(data: AssignmentProjectEmployeeDTO) {
		this.allProjectAssignments.forEach((element) => {
			if (element.employee.id === data.employee.id) {
				element.checked = data.checked;
			}
		});
		this.newProjectEnd.updateValueAndValidity();
	}

	validateNewProjectEnd(control: UntypedFormControl) {
		if (this.moveProject) {
			return null;
		}

		const inputEnd = Date.parse(control.value);
		let error = null;

		if (this.oldProjectEnd) {
			const oldProjectEnd = Date.parse(this.oldProjectEnd);

			if (oldProjectEnd >= inputEnd) {
				error = {oldProjectEndAfterNew: {value: control.value}};
			}
		}

		if (this.allProjectAssignments) {
			const assignmentCutOffByNewEnd = this.allProjectAssignments.filter(
				(assignment) => assignment.checked && Date.parse(assignment.effectiveUntil) > inputEnd,
			);

			if (assignmentCutOffByNewEnd.length > 0) {
				error = {newProjectEndCutsOffAssignment: assignmentCutOffByNewEnd};
			}
		}

		return error;
	}

	validateNewProjectStart(control: UntypedFormControl) {
		const inputStart = Date.parse(control.value);
		let error = null;

		if (this.newProjectEnd.value) {
			const inputEnd = Date.parse(this.newProjectEnd.value);

			if (inputStart > inputEnd) {
				error = {startAfterEnd: true};
			}
		}

		return error;
	}

	validateNotWithinOtherProjectTimeSlice(control: UntypedFormControl) {
		if (!this.moveProject) {
			return null;
		}

		const input = Date.parse(control.value);
		let error = null;

		const timeSliceResult = this.projectTimeSlices.find(
			(timeSlice) =>
				timeSlice !== this.selectedProjectTimeSlice &&
				Date.parse(timeSlice.effectiveFrom) <= input &&
				Date.parse(timeSlice.effectiveUntil) >= input,
		);

		if (timeSliceResult) {
			error = {withinExistingTimeSlice: timeSliceResult};
		}

		return error;
	}

	onNewProjectStartChanged(inputValue: string) {
		const formattedValue = this.getFormattedDateInput(inputValue);
		if (formattedValue === null) {
			return;
		}

		// emitModelToViewChange: false, sonst wird das Eingabefeld während des Tippens neu formatiert
		this.newProjectStart.setValue(formattedValue, {emitModelToViewChange: false});
		this.updateAssignmentProjectStart();

		if (this.moveProject && formattedValue) {
			if (!this.newProjectEnd.value || Date.parse(formattedValue) > Date.parse(this.newProjectEnd.value)) {
				this.newProjectEnd.setValue(formattedValue);
				this.updateAssignmentProjectEnd();
			}
		}
	}

	onNewProjectEndChanged(inputValue: string) {
		const formattedValue = this.getFormattedDateInput(inputValue);
		if (formattedValue === null) {
			return;
		}

		this.newProjectEnd.setValue(formattedValue, {emitModelToViewChange: false});
		this.updateAssignmentProjectEnd();
		this.newProjectStart.updateValueAndValidity();

		if (this.moveProject && formattedValue) {
			if (Date.parse(this.newProjectStart.value) > Date.parse(formattedValue)) {
				this.newProjectStart.setValue(formattedValue);
				this.updateAssignmentProjectStart();
			}
		}
	}

	/**
	 * Liefert das Datum im Format YYYY-MM-DD, "" für eine leere Eingabe und null für eine unvollständige Eingabe.
	 * Strikt geparst, da der DateAdapter z.B. "31.01.202" als Jahr 202 akzeptiert.
	 */
	private getFormattedDateInput(inputValue: string): string | null {
		if (!inputValue) {
			return "";
		}

		const date = moment(inputValue, "DD.MM.YYYY", true);
		return date.isValid() ? date.format(this.dateFormat) : null;
	}

	/**
	 * Bei unvollständiger Eingabe enthält das Control das Moment-Objekt des Datepickers statt eines Strings.
	 */
	private getFormattedControlValue(control: UntypedFormControl): string {
		return control.value ? moment(control.value).format(this.dateFormat) : "";
	}

	private getAssignmentProjectEmployees() {
		this.allProjectAssignments = undefined;
		this.loading = true;
		this._projectService.getAssignmentEmployeeProjectInfo(this.project._links.self.href).then(
			(assigned: AssignmentProjectEmployee[]) => {
				if (this.dialogDestroyed) {
					return;
				}

				this.allProjectAssignments = assigned;
				this.noAssignments = !assigned || assigned.length === 0;
				this.loading = false;

				this.updateAssignmentTableRows();
				this.allProjectAssignments.forEach((element) => (element.checked = true));
			},
			() => {
				this.loading = false;
			},
		);
	}

	private getProjectProbabilities() {
		this._projectService.getProjectProbabilities(this.project, false).then((timeSlices: ProjectTimeSlice[]) => {
			if (timeSlices && timeSlices.length > 0) {
				timeSlices.sort(this._projectService.sortByEffectiveFrom);
				this.projectTimeSlices = timeSlices;
				this.selectedProjectTimeSlice = this.projectTimeSlices[timeSlices.length - 1];

				this.oldProjectEnd = this.selectedProjectTimeSlice.effectiveUntil;
				this.oldProjectProbability = this.selectedProjectTimeSlice.probability;
				this.newProjectProbability.setValue(this.oldProjectProbability);
				this.newProjectProbability.updateValueAndValidity();
				this.updateAssignmentTableRows();
			} else {
				this.projectTimeSlices = [];
			}
		});
	}

	private getEffectiveFrom(employeeID: number): string {
		let until: string;

		this.allProjectAssignments.forEach((assignment) => {
			if (employeeID === assignment.employee.id) {
				let temp = moment(assignment.effectiveUntil);

				if (!until || moment(until) < temp) {
					until = assignment.effectiveUntil;
				}
			}
		});

		const from = moment(until);
		from.add(1, "day");

		return from.format(this.dateFormat);
	}
}

export interface AssignmentProjectEmployeeDTO {
	employee: Employee;
	project: Project;
	effectiveFrom: UntypedFormControl;
	effectiveUntil: UntypedFormControl;
	capacity: UntypedFormControl;
	existingAssignmentID: number;
	checked?: boolean;
}
